<script setup lang="ts">
    import { ref, computed, toRef } from 'vue'
    import { guid } from '@common/Guid'
    import { isNullOrWhitespace } from '@common/Common'
    import { ProjectUser } from '@Projects/Model'
    import type { ProjectUsersFromStringBuilderViewModel } from '@ProjectUsers/Components/ProjectUsersFromStringBuilder'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: ProjectUsersFromStringBuilderViewModel
    }>()

    const emit = defineEmits<{
        (e: 'create-project-users', users: ProjectUser[]): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const operationInProgress = computed(() => vm.value.operationInProgress)
    const tags = computed(() => vm.value.tags)

    // writable computed (replaces getter + setter)
    const step = computed<number>({
        get: () => vm.value.step,
        set: v => { vm.value.step = v }
    })

    // ---------------- local state ----------------

    const NamesOrEmails = ref<string | null>(null)
    const ProjectUsers = ref<ProjectUserVM[]>([])

    // ---------------- actions ----------------

    function preview()
    {
        if (!NamesOrEmails.value)
            return

        const lines = NamesOrEmails.value.split(/\r\n|\r|\n/g)
        if (!lines?.length)
            return

        ProjectUsers.value = []

        for (const line of lines)
        {
            if (!line)
                continue

            if (line.includes('\t'))
            {
                const parts = line.split('\t')
                const part1 = parts[0]
                const part2 = parts[1]

                const tagsStr = parts.length === 3 ? parts[2] : null
                let parsedTags =
                    isNullOrWhitespace(tagsStr)
                        ? null
                        : tagsStr!
                            .split(';')
                            .map(t => t.trim())
                            .filter(t => !isNullOrWhitespace(t))

                if (parsedTags)
                {
                    parsedTags = parsedTags.filter(t => tags.value.includes(t))
                }

                if (part1.includes('@'))
                {
                    ProjectUsers.value.push(ProjectUserVM.Create(part2, part1, parsedTags))
                }
                else if (part2.includes('@'))
                {
                    ProjectUsers.value.push(ProjectUserVM.Create(part1, part2, parsedTags))
                }
                else
                {
                    ProjectUsers.value.push(ProjectUserVM.Create(line, null, parsedTags))
                }
            }
            else
            {
                ProjectUsers.value.push(
                    ProjectUserVM.Create(
                        line,
                        line.includes('@') ? line : null,
                        null
                    )
                )
            }
        }

        step.value = 2
    }

    function back()
    {
        step.value = 1
    }

    function handleCreateProjectUsers()
    {
        const projectUsers = ProjectUsers.value
            .filter(p => p.Name)
            .map(p => p.GetData())

        vm.value.operationInProgress = true
        ProjectUsers.value = []
        NamesOrEmails.value = null

        emit('create-project-users', projectUsers)
    }

    function close()
    {
        vm.value.close()
    }

    // ---------------- local helper class ----------------

    class ProjectUserVM
    {
        LocalId: string
        Name: string | null
        Email: string | null
        Tags: string[] | null

        private constructor(
            name: string | null,
            email: string | null,
            tags: string[] | null,
            localId: string
        )
        {
            this.Name = name
            this.Email = email
            this.Tags = tags
            this.LocalId = localId
        }

        static Create(
            name: string | null,
            email: string | null,
            tags: string[] | null
        ): ProjectUserVM
        {
            return new ProjectUserVM(name, email, tags, guid())
        }

        GetData(): ProjectUser
        {
            const pu = new (ProjectUser as any)() as ProjectUser
            pu.ProjectUserId = 0
            pu.LocalId = this.LocalId
            pu.Name = this.Name
            pu.Email = this.Email
            pu.Tags = this.Tags
            return pu
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Add</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div v-if="step === 1">
                            <div style="overflow-x: hidden;">
                                <label>
                                    Insert as many respondent names as you like, with each on a new line
                                </label>
                                <textarea v-model.trim="NamesOrEmails"
                                          class="form-control"
                                          rows="8"
                                          placeholder="you can copy and paste here" />
                            </div>
                        </div>

                        <div v-if="step === 2">
                            <div class="row"
                                 style="margin-bottom: 3px;"
                                 v-for="p in ProjectUsers"
                                 :key="p.LocalId">
                                <div class="col-md-4">
                                    <input type="text"
                                           v-model.trim="p.Name"
                                           class="form-control"
                                           placeholder="Required name">
                                </div>

                                <div class="col-md-4">
                                    <input type="email"
                                           v-model.trim="p.Email"
                                           class="form-control"
                                           placeholder="Optional email">
                                </div>

                                <div class="col-md-4">
                                    <span class="badge badge-secondary"
                                          v-for="t in p.Tags"
                                          :key="t">
                                        {{t}}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button v-if="step === 1"
                                @click="preview"
                                class="btn btn-primary">
                            Preview
                        </button>

                        <button v-if="step === 2"
                                @click="back"
                                class="btn btn-outline-secondary"
                                :disabled="operationInProgress">
                            Back
                        </button>

                        <button v-if="step === 2"
                                @click="handleCreateProjectUsers"
                                :disabled="operationInProgress"
                                type="button"
                                class="btn btn-primary">
                            <span v-if="operationInProgress">
                                <i class="fa fa-spinner fa-spin"></i> Please wait...
                            </span>
                            <span v-else>Save</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
